import type { SlaContractStatus } from '@evimo/types/enums/sla-contract-status';

/**
 * ISO weekday numbers stored on the contract.
 * 1 = Monday … 7 = Sunday. The plan does not assign these integers.
 */
export const MAINTENANCE_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export type MaintenanceWeekday = (typeof MAINTENANCE_WEEKDAYS)[number];

export const MAINTENANCE_SLA_ON_SITE_LEVELS = [
  'N1',
  'N2',
  'N3',
  'N4',
  'N5',
] as const;

export const MAINTENANCE_SLA_REMOTE_LEVELS = [
  'R1',
  'R2',
  'R3',
  'R4',
  'R5',
] as const;

export const MAINTENANCE_SLA_LEVEL_CODES = [
  ...MAINTENANCE_SLA_ON_SITE_LEVELS,
  ...MAINTENANCE_SLA_REMOTE_LEVELS,
] as const;

export type MaintenanceSlaLevelCode =
  (typeof MAINTENANCE_SLA_LEVEL_CODES)[number];

export const MAINTENANCE_SLA_CATEGORIES = ['ON_SITE', 'REMOTE'] as const;

export type MaintenanceSlaCategory =
  (typeof MAINTENANCE_SLA_CATEGORIES)[number];

export const MAINTENANCE_SLA_PRIORITIES = ['STANDARD', 'EMERGENCY'] as const;

export type MaintenanceSlaPriority =
  (typeof MAINTENANCE_SLA_PRIORITIES)[number];

export const MAINTENANCE_SLA_TIMER_KINDS = [
  'TAKE_CHARGE',
  'INTERVENTION',
  'RESOLUTION',
] as const;

export type MaintenanceSlaTimerKind =
  (typeof MAINTENANCE_SLA_TIMER_KINDS)[number];

export const MAINTENANCE_SLA_CLOCK_STARTS = [
  'INCIDENT_DETECTED',
  'FAULT_REPORTED',
  'TICKET_CREATED',
  'TICKET_TAKEN_IN_CHARGE',
  'FIRST_INTERVENTION',
] as const;

export type MaintenanceSlaClockStart =
  (typeof MAINTENANCE_SLA_CLOCK_STARTS)[number];

export const MAINTENANCE_SLA_CALENDAR_MODES = [
  'CONTINUOUS_24_7',
  'EXCLUDING_WEEKENDS_AND_HOLIDAYS',
  'BUSINESS_HOURS',
] as const;

export type MaintenanceSlaCalendarMode =
  (typeof MAINTENANCE_SLA_CALENDAR_MODES)[number];

export const MAINTENANCE_SLA_STOP_SIGNALS = [
  'RESTORED_AT',
  'DECLARED_CLOSED_AT',
] as const;

export type MaintenanceSlaResolutionStop =
  (typeof MAINTENANCE_SLA_STOP_SIGNALS)[number];

export const MAINTENANCE_SLA_SCOPE_DIMENSIONS = [
  'MANUFACTURER',
  'MODEL',
  'COMMUNE',
  'ASSET',
] as const;

export type MaintenanceSlaScopeDimension =
  (typeof MAINTENANCE_SLA_SCOPE_DIMENSIONS)[number];

export const DEPRECATED_MAINTENANCE_SCOPE_DIMENSIONS = [
  'DEPARTMENT',
  'POSTCODE',
] as const;

export type DeprecatedMaintenanceScopeDimension =
  (typeof DEPRECATED_MAINTENANCE_SCOPE_DIMENSIONS)[number];

export const ALL_MAINTENANCE_SCOPE_DIMENSIONS = [
  ...MAINTENANCE_SLA_SCOPE_DIMENSIONS,
  ...DEPRECATED_MAINTENANCE_SCOPE_DIMENSIONS,
] as const;

export type AllMaintenanceScopeDimension =
  (typeof ALL_MAINTENANCE_SCOPE_DIMENSIONS)[number];

export function isDeprecatedMaintenanceScopeDimension(
  dim: string,
): dim is DeprecatedMaintenanceScopeDimension {
  return (
    DEPRECATED_MAINTENANCE_SCOPE_DIMENSIONS as readonly string[]
  ).includes(dim);
}

export const MAINTENANCE_SLA_INTERVENTION_MODES = [
  'ONSITE',
  'REMOTE',
  'BOTH',
] as const;

export type MaintenanceSlaInterventionMode =
  (typeof MAINTENANCE_SLA_INTERVENTION_MODES)[number];

export const MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES = [
  'ONSITE',
  'REMOTE',
] as const;

export type MaintenanceSlaScopeInterventionMode =
  (typeof MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES)[number];

export const MAINTENANCE_SLA_SCOPE_EFFECTS = ['INCLUDE', 'EXCLUDE'] as const;

export type MaintenanceSlaScopeEffect =
  (typeof MAINTENANCE_SLA_SCOPE_EFFECTS)[number];

export const MAINTENANCE_TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Berlin',
  'UTC',
] as const;

export type MaintenanceSlaDraftPricingCombination = {
  id: string;
  takeChargeMinutes: number | null;
  interventionMinutes: number | null;
  resolutionMinutes: number | null;
  priceAmount: number | null;
};

export type MaintenanceSlaDraftLevel = {
  code: MaintenanceSlaLevelCode;
  penaltyClauseText: string;
  penaltyAmount: number | null;
  timers: MaintenanceSlaDraftTimer[];
  pricingCombinations: MaintenanceSlaDraftPricingCombination[];
};

/** How EVIMO ends a resolution timer. Same for every contract. Not chosen on the form. */
export const EVIMO_RESOLUTION_STOP_SIGNAL = 'DECLARED_CLOSED_AT' as const;

export type MaintenanceSlaDraftTimer = {
  included: boolean;
  kind: MaintenanceSlaTimerKind;
  clockStart: MaintenanceSlaClockStart | null;
  calendarMode: MaintenanceSlaCalendarMode | null;
};

export type MaintenanceSlaDraftTerm = {
  priority: MaintenanceSlaPriority;
  penaltyClauseText: string;
  penaltyAmount: number | null;
  timers: MaintenanceSlaDraftTimer[];
  pricingCombinations: MaintenanceSlaDraftPricingCombination[];
};

export type MaintenanceSlaDraftGroup = {
  id: string;
  category: MaintenanceSlaCategory;
  name: string;
  levels: MaintenanceSlaDraftLevel[];
  standard: MaintenanceSlaDraftTerm;
  emergency: MaintenanceSlaDraftTerm | null;
};

export type MaintenanceSlaScopeOptionClaim = {
  partnerName: string;
  relationshipId: string;
  interventionMode?: MaintenanceSlaScopeInterventionMode | null;
};

export type MaintenanceSlaScopeOption = {
  value: string;
  label: string;
  claimedBy?: MaintenanceSlaScopeOptionClaim;
  claims?: MaintenanceSlaScopeOptionClaim[];
};

export type MaintenanceSlaScopeHierarchyAsset = {
  id: string;
  assetModelId: string | null;
  manufacturerId: string | null;
  cityCode: string | null;
};

export type MaintenanceSlaScopeOptions = {
  manufacturers: MaintenanceSlaScopeOption[];
  models: MaintenanceSlaScopeOption[];
  departments: MaintenanceSlaScopeOption[];
  communes: MaintenanceSlaScopeOption[];
  postcodes: MaintenanceSlaScopeOption[];
  assets: MaintenanceSlaScopeOption[];
  assetHierarchy: MaintenanceSlaScopeHierarchyAsset[];
};

export type MaintenanceSlaDraftScopeRule = {
  id: string;
  dimension: AllMaintenanceScopeDimension;
  effect: MaintenanceSlaScopeEffect;
  values: string[];
  interventionMode: MaintenanceSlaScopeInterventionMode | null;
};

/** Dates and status of the existing O&M partnership. Not edited on the rules form. */
export type MaintenancePartnershipValidity = {
  status: SlaContractStatus;
  validFrom: string | Date;
  validTo: string | Date | null;
};

export type MaintenanceSlaDraft = {
  timezone: string;
  businessHoursStart: string | null;
  businessHoursEnd: string | null;
  workingDays: number[];
  holidayCalendarCode: string;
  exclusionsText: string;
  recurrenceWindowDays: number | null;
  restoredAvailableHours: number | null;
  restoredAvailableMinutes: number | null;
  priceCurrency: string;
  excludeHolidays: boolean;
  levels: MaintenanceSlaDraftLevel[];
  scopeRules: MaintenanceSlaDraftScopeRule[];
  interventionMode: MaintenanceSlaInterventionMode | null;
};

export type MaintenanceSlaSavedPricingCombination = {
  id: string;
  takeChargeMinutes: number | null;
  interventionMinutes: number | null;
  resolutionMinutes: number | null;
  priceAmount: number | null;
};

export type MaintenanceSlaSavedLevel = {
  code: MaintenanceSlaLevelCode;
  penaltyClauseText: string | null;
  penaltyAmount: number | null;
  timers: MaintenanceSlaSavedTimer[];
  pricingCombinations: MaintenanceSlaSavedPricingCombination[];
};

export type MaintenanceSlaSavedTimer = {
  kind: MaintenanceSlaTimerKind;
  clockStart: MaintenanceSlaClockStart | null;
  calendarMode: MaintenanceSlaCalendarMode | null;
};

export type MaintenanceSlaSavedTerm = {
  priority: MaintenanceSlaPriority;
  penaltyClauseText: string | null;
  penaltyAmount: number | null;
  timers: MaintenanceSlaSavedTimer[];
  pricingCombinations: MaintenanceSlaSavedPricingCombination[];
};

export type MaintenanceSlaSavedGroup = {
  id: string;
  category: MaintenanceSlaCategory;
  name: string;
  levels: MaintenanceSlaSavedLevel[];
  standard: MaintenanceSlaSavedTerm;
  emergency: MaintenanceSlaSavedTerm | null;
};

export type MaintenanceSlaSavedScopeRule = {
  id: string;
  dimension: AllMaintenanceScopeDimension;
  effect: MaintenanceSlaScopeEffect;
  value: string | null;
  interventionMode: MaintenanceSlaScopeInterventionMode | null;
};

export const MAINTENANCE_RULES_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export type MaintenanceRulesStatus =
  (typeof MAINTENANCE_RULES_STATUSES)[number];

/** Saved rules for one O&M relationship. Dates come from the partnership. */
export type MaintenanceSlaContractRecord = {
  relationshipId: string;
  status: SlaContractStatus;
  rulesStatus: MaintenanceRulesStatus;
  validFrom: string;
  validTo: string | null;
  timezone: string;
  businessHoursStart: string | null;
  businessHoursEnd: string | null;
  workingDays: number[];
  holidayCalendarCode: string;
  exclusionsText: string | null;
  resolutionStopSignal: MaintenanceSlaResolutionStop;
  recurrenceWindowDays: number | null;
  restoredAvailableMinutes: number | null;
  priceCurrency: string;
  excludeHolidays: boolean;
  levels: MaintenanceSlaSavedLevel[];
  scopeRules: MaintenanceSlaSavedScopeRule[];
  interventionMode: MaintenanceSlaInterventionMode | null;
};

export const MAINTENANCE_CONTRACT_ISSUE_CODES = [
  'restoredMinutes',
  'pricingCombination',
  'pricingCombinationPrice',
  'timerClock',
  'timerCalendar',
  'scopeValue',
  'scopeInterventionModeRequired',
  'scopeHierarchyRedundancy',
  'scopeEffectConflict',
] as const;

export type MaintenanceContractIssueCode =
  (typeof MAINTENANCE_CONTRACT_ISSUE_CODES)[number];

export type MaintenanceContractIssue = {
  code: MaintenanceContractIssueCode;
  levelCode?: MaintenanceSlaLevelCode;
  kind?: MaintenanceSlaTimerKind;
  ruleId?: string;
};

export const CONFLICT_REASONS = [
  'LITERAL_CLAIMED_BY_OTHER',
  'GEO_DOWN_COMMUNE_HAS_CLAIMED_ASSET',
  'GEO_UP_ASSET_INSIDE_CLAIMED_COMMUNE',
  'MANUFACTURER_DOWN_OWNED_MANUFACTURER_HAS_CLAIMED_ASSET',
  'MANUFACTURER_UP_ASSET_OWNED_BY_OTHER_MANUFACTURER_CLAIMED',
  'EXCLUDE_OVER_OWNED',
] as const;

export type ConflictReason = (typeof CONFLICT_REASONS)[number];

export type ConflictingPartnerScopeItem = {
  dimension: MaintenanceSlaScopeDimension;
  value: string;
  claimedByPartnerName: string;
  claimedByRelationshipId: string;
  conflictReason: ConflictReason;
};
